import styled from 'styled-components';
import { useRef } from 'react';
import { RecentCampaignAlert as RecentCampaignAlertType } from 'modules/missionControl/types/health';
import { colors, spacing } from 'ui/theme';
import { RecentCampaignAlert } from './RecentCampaignAlert';
import { getAlertHistoryItemLabelDisplayText } from 'modules/campaigns/lib/components/AlertsHistorySlideOver/AlertsHistorySlideOver.utils';
import { ScrollArea } from 'ui/scrollArea';
import { useInfiniteScrolling } from 'utils/hooks/useInfiniteScrolling';
import { SkeletonContainer } from 'ui/skeleton';
import { RelativeTime } from 'ui/utility/relativeTime';

const AlertsContainer = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const AlertsScrollArea = styled(ScrollArea)`
  width: 100%;
  height: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${spacing.base};

  & [data-radix-scroll-area-viewport] > div > *:not(:first-child) {
    position: relative;

    /* Display a separator between alerts */
    &::before {
      content: "";
      top: 0;
      position: absolute;
      height: ${spacing.single};
      left: ${spacing.plusHalf};
      width: calc(100% - 2 * ${spacing.plusHalf});
      background: ${colors.borderPrimarySubtle};
      transition:
        width 0.2s ease,
        left 0.2s ease,
        background-color 0.2s ease;
    }
  }

  /* Override styles for hovered/focused elements and their next siblings */
  & [data-radix-scroll-area-viewport] > div > *:is(:hover, :focus, :focus-visible)::before,
  & [data-radix-scroll-area-viewport] > div > *:is(:hover, :focus, :focus-visible) + *::before {
    left: 0;
    width: 100%;
  }
`;

const AlertsContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const InfiniteScrollTrigger = styled.div`
  height: 1px;
  width: 100%;
`;

const LoadingMoreIndicator = styled(SkeletonContainer)`
  height: ${spacing.x5};
`;

type RecentCampaignAlertsListProps = {
  recentAlerts: RecentCampaignAlertType[];
  setSelectedAlert: (alert: RecentCampaignAlertType) => void;
  hasMore: boolean;
  fetchMore: () => Promise<void>;
  isLoading: boolean;
  isFetchingMore: boolean;
};

export const RecentCampaignAlertsList = ({
  recentAlerts,
  setSelectedAlert,
  hasMore,
  fetchMore,
  isLoading,
  isFetchingMore,
}: RecentCampaignAlertsListProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const handleFetchMore = async () => {
    if (isFetchingMore || !hasMore) return;
    try {
      await fetchMore();
    } catch (error) {
      console.error('Error fetching more alerts:', error);
    }
  };

  useInfiniteScrolling({
    container: containerRef,
    trigger: triggerRef,
    canFetch: hasMore && !isLoading,
    onFetch: handleFetchMore,
    isFetching: isFetchingMore,
    threshold: 0.8,
    rootMargin: '20px',
  });

  return (
    <AlertsContainer ref={containerRef} data-test="recent-campaign-alerts-list">
      <AlertsScrollArea scrollMasks="bottom" scrollMaskSize="32px">
        <AlertsContent>
          {recentAlerts.map((alert: RecentCampaignAlertType, index) => {
            const { frequencyText, thresholdText } = getAlertHistoryItemLabelDisplayText(
              alert,
              false,
            );
            const caption = <RelativeTime timestamp={alert.triggeredTime} />;

            return (
              <RecentCampaignAlert
                key={alert.id}
                data-test={`alert-history-item-${index}`}
                resourceName={alert.campaignName}
                content={`${thresholdText.toLowerCase()} ${frequencyText}`}
                caption={caption}
                onClick={() => setSelectedAlert(alert)}
              />
            );
          })}

          {hasMore && (
            <>
              <InfiniteScrollTrigger ref={triggerRef} />
              {isFetchingMore && (
                <LoadingMoreIndicator data-test="loading-more-alerts" isLoading>
                  Loading more alerts...
                </LoadingMoreIndicator>
              )}
            </>
          )}
        </AlertsContent>
      </AlertsScrollArea>
    </AlertsContainer>
  );
};
